import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Box, Button } from "@mui/material";
import {
  employeeFieldConfiguration,
  employeeInitialValues,
  makeFieldConfigDynamic,
} from "../../../formConfigs/HR/employeeFieldConfig";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import { OptionType } from "../../../interfaces/Fleet/IGenericFormPopupProps";
import GenericFormPopup from "../../Fleet/GenericFormPopup/GenericFormPopup";
import { getId } from "../UpdateEmployeePopup/UpdateEmployeePopup";
import { validateAndFormatChildrenDetails } from "../../../helpers/employeeChildrenValidation";
import { normalizeEmployeeGender } from "../../../data/HR/EmployeeGender";

export const fetchManagers = async (token: string) => {
  const response = await axios.get(`${apiVariable}/getAllManagers`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.map((manager: any) => ({
    label: `${manager.firstName} ${manager.lastName} | ${manager.position}`,
    id: manager.id.toString(),
  }));
};

const EmployeePopup = () => {
  const [open, setOpen] = useState(false);
  const [reportsToData, setReportsToData] = useState<OptionType[]>([]);
  const [secondLevelManagerData, setSecondLevelManagerData] = useState<
    OptionType[]
  >([]);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSnackbarClose = () => setStatus("idle");

  const queryClient = useQueryClient();
  const { token } = useAuth();

  const {
    data: managersData,
    isError,
    error,
  } = useQuery({
    queryKey: ["managers"],
    queryFn: () => fetchManagers(token!),
    enabled: !!token,
  });

  useEffect(() => {
    if (managersData) {
      setReportsToData(managersData);
      setSecondLevelManagerData(managersData);
    }
  }, [managersData]);

  useEffect(() => {
    if (isError && error) {
      setStatus("error");
      setErrorMessage(
        (error as any).response?.data || "Failed to fetch managers"
      );
    }
  }, [isError, error]);

  const employeeMutation = useMutation({
    mutationFn: (data: any) =>
      axios.post(`${apiVariable}/employee`, data, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    onSuccess: () => {
      setStatus("success");
      handleClose();
      queryClient.invalidateQueries({ queryKey: ["employees"] });
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to create employee");
    },
  });

  const submitHandler = (data: any) => {
    const requiredFields = [
      "firstName",
      "lastName",
      "position",
      "department",
      "contractType",
      "payFrequency",
      "startDate",
      "email",
      "phoneNumber",
      "isMarried",
      "childrenCount",
      "employeeId",
    ];

    const isMissingValue = (value: any) =>
      value === undefined || value === null || value === "";

    const missingFields = requiredFields.filter((field) =>
      isMissingValue(data[field])
    );

    if (missingFields.length > 0) {
      setStatus("error");
      setErrorMessage("Please fill in all required fields.");
      return;
    }

    const {
      isValid,
      message: childrenMessage,
      count,
      formattedSexes,
    } = validateAndFormatChildrenDetails(
      data.childrenCount,
      data.childrenSexes
    );

    if (!isValid) {
      setStatus("error");
      setErrorMessage(childrenMessage || "Invalid children details.");
      return;
    }

    const submitData = {
      ...data,
      gender: normalizeEmployeeGender(data.gender),
      reportsToId: getId(data.reportsToId),
      secondLevelManager: getId(data.secondLevelManager),
      terminationDate:
        data.terminationDate === "" ? null : data.terminationDate,
      childrenCount: count,
      childrenSexes: formattedSexes || null,
    };

    employeeMutation.mutate(submitData);
  };

  const dynamicFields = makeFieldConfigDynamic(employeeFieldConfiguration);

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        Create Employee
      </Button>
      <GenericFormPopup
        formFields={dynamicFields}
        initialValues={employeeInitialValues}
        title="Employee Form"
        onSubmit={submitHandler}
        open={open}
        onClose={handleClose}
        autocompleteData={{
          reportsTo: reportsToData,
          secondLevelManager: secondLevelManagerData,
        }}
        loading={employeeMutation.isPending}
      />
      <StatusSnackbar
        status={status}
        successMessage="Employee created successfully"
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default EmployeePopup;
